import { Plate } from '../components/Card';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../api';

// ─── Theme helpers ────────────────────────────────────────────────────────────

const severityColor = {
  low: 'var(--text2)',
  medium: '#e08a00',
  high: '#e04848',
  critical: '#e04848',
};

const kindLabel = {
  duplicate: 'Дубликат ключа',
  auto: 'Автоключ / чип',
  emergency: 'Вскрытие',
  corporate: 'Корпоратив',
  general: 'Общий',
};

const statusColor = {
  pending: 'var(--text2)',
  won: '#0e9f6e',
  lost: '#e04848',
};

// ─── Primitive UI components ──────────────────────────────────────────────────

function Card({ title, value, sub, color = 'var(--accent)' }) {
  return (
    <div style={{
      background: 'var(--bg2)',
      borderRadius: 12,
      padding: '14px 16px',
      border: '1px solid var(--border)',
    }}>
      <div style={{ fontSize: 12, color: 'var(--text2)' }}>{title}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color, marginTop: 4 }}>{value}</div>
      {sub ? <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4 }}>{sub}</div> : null}
    </div>
  );
}

function Section({ title, action, children }) {
  return (
    <div style={{
      background: 'var(--bg2)',
      borderRadius: 14,
      border: '1px solid var(--border)',
      padding: 20,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 16, color: 'var(--text)' }}>{title}</h3>
        <div style={{ marginLeft: 'auto' }}>{action}</div>
      </div>
      {children}
    </div>
  );
}

function Btn({ children, onClick, disabled = false, tone = 'primary', size = 'md' }) {
  const bg = {
    primary: 'var(--accent)',
    secondary: 'var(--accent)',
    danger: 'var(--red)',
    ghost: 'var(--bg3)',
  }[tone];

  const pad = size === 'sm' ? '7px 12px' : '10px 16px';

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        border: tone === 'ghost' ? '1px solid var(--border)' : 'none',
        borderRadius: 10,
        padding: pad,
        color: tone === 'ghost' ? 'var(--text1)' : '#fff',
        background: disabled ? 'var(--text2)' : bg,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontWeight: 700,
        fontSize: 13,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span style={{ fontSize: 12, color: 'var(--text2)', fontWeight: 600 }}>{label}</span>
      {children}
    </label>
  );
}

function Input({ value, onChange, placeholder }) {
  return (
    <input
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder || ''}
      style={{
        background: 'var(--bg3)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: '8px 10px',
        color: 'var(--text)',
        fontSize: 13,
        outline: 'none',
      }}
    />
  );
}

function Select({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        background: 'var(--bg3)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: '8px 10px',
        color: 'var(--text)',
        fontSize: 13,
        outline: 'none',
      }}
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function Textarea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      style={{
        background: 'var(--bg3)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: '8px 10px',
        color: 'var(--text)',
        fontSize: 13,
        outline: 'none',
        resize: 'vertical',
        fontFamily: 'inherit',
      }}
    />
  );
}

function Badge({ label, color = 'var(--text2)' }) {
  return (
    <span style={{
      background: `${color}22`,
      color,
      borderRadius: 999,
      padding: '3px 8px',
      fontSize: 11,
      fontWeight: 700,
    }}>
      {label}
    </span>
  );
}

function AlertBox({ message, tone = 'warning' }) {
  const colors = {
    error: { bg: 'rgba(224,72,72,0.08)', border: 'rgba(224,72,72,0.25)', text: '#e04848' },
    warning: { bg: 'rgba(224,138,0,0.08)', border: 'rgba(224,138,0,0.25)', text: '#e08a00' },
    success: { bg: 'rgba(14,159,110,0.08)', border: 'rgba(14,159,110,0.25)', text: '#0e9f6e' },
    info: { bg: 'rgba(0,167,220,0.08)', border: 'rgba(0,167,220,0.25)', text: '#0089b6' },
  };
  const c = colors[tone] || colors.warning;
  return (
    <div style={{
      background: c.bg,
      border: `1px solid ${c.border}`,
      borderRadius: 10,
      padding: '10px 12px',
      color: c.text,
      fontSize: 13,
    }}>
      {message}
    </div>
  );
}

// ─── Async helper ──────────────────────────────────────────────────────────────

async function getOrNull(request) {
  try {
    const res = await request;
    return res.data;
  } catch {
    return null;
  }
}

// ─── Tab: Обзор ───────────────────────────────────────────────────────────────

function TabOverview() {
  const [boot, setBoot] = useState(null);
  const [bootLoading, setBootLoading] = useState(true);
  const [bootError, setBootError] = useState('');
  const [overview, setOverview] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [controlRunning, setControlRunning] = useState('');
  const [controlResult, setControlResult] = useState(null);
  const [slaRunning, setSlaRunning] = useState(false);
  const [slaResult, setSlaResult] = useState(null);

  const loadBootstrap = useCallback(async () => {
    setBootLoading(true);
    setBootError('');
    try {
      const res = await api.get('/crm/bitrix-agent/bootstrap', { timeout: 10000 });
      setBoot(res.data);
    } catch (error) {
      setBootError(error?.message || 'Не удалось загрузить модуль.');
    } finally {
      setBootLoading(false);
    }
  }, []);

  const loadDetails = useCallback(async () => {
    setDetailsLoading(true);
    setDetailsError('');
    try {
      const [overviewData, routesData] = await Promise.all([
        getOrNull(api.get('/crm/bitrix-agent/overview', { timeout: 20000 })),
        getOrNull(api.get('/crm/bitrix-agent/notification-routes', { timeout: 10000 })),
      ]);
      if (!overviewData && !routesData) {
        setDetailsError('Live-данные Bitrix сейчас недоступны. Базовый модуль продолжает работать.');
        return;
      }
      setOverview({
        ...(overviewData || {}),
        notification_routes: routesData || overviewData?.notification_routes || [],
      });
    } catch (error) {
      setDetailsError(error?.message || 'Не удалось загрузить live-данные.');
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  useEffect(() => { loadBootstrap(); }, [loadBootstrap]);

  const runControl = async (mode) => {
    setControlRunning(mode);
    try {
      const res = await api.post('/crm/bitrix-agent/control', { mode }, { timeout: 30000 });
      setControlResult(res.data);
    } catch (error) {
      setControlResult({ mode, error: error?.message || 'Ошибка.' });
    } finally {
      setControlRunning('');
    }
  };

  const runSla = async () => {
    setSlaRunning(true);
    try {
      const res = await api.post('/crm/bitrix-agent/sla/run', {}, { timeout: 30000 });
      setSlaResult(res.data);
    } catch (error) {
      setSlaResult({ error: error?.message || 'Ошибка SLA.' });
    } finally {
      setSlaRunning(false);
    }
  };

  if (bootLoading) {
    return <div style={{ padding: 24, color: 'var(--text2)' }}>Загружаем модуль Bitrix…</div>;
  }
  if (bootError) {
    return (
      <div style={{ padding: 24 }}>
        <AlertBox tone="error" message={`Модуль Bitrix не загрузился: ${bootError}`} />
      </div>
    );
  }

  const summary = boot?.summary;
  const masters = boot?.masters || [];
  const decisions = boot?.decisions || [];
  const leaderTasks = boot?.leader_tasks || [];
  const liveSummary = overview?.summary || {};
  const liveSla = slaResult || overview?.sla;

  const metricCards = [
    { title: 'Решений AI', value: summary?.handled_by_ai || 0, sub: 'в журнале', color: '#0e9f6e' },
    { title: 'Целевых', value: summary?.target_count || 0, sub: 'квалифицированных', color: '#00a7dc' },
    { title: 'Назначено', value: summary?.assigned_count || 0, sub: 'на филиалы', color: '#00a7dc' },
    { title: 'Успех / Провал', value: `${summary?.won_count || 0} / ${summary?.lost_count || 0}`, sub: 'won / lost', color: '#e08a00' },
    { title: 'Спам', value: summary?.spam_count || 0, sub: 'помечено', color: '#e04848' },
    { title: 'Точек', value: summary?.masters_total || 0, sub: 'профилей', color: '#00a7dc' },
    { title: 'Задачи руков.', value: summary?.leader_open_total || 0, sub: 'открытых', color: '#e08a00' },
    { title: 'SLA событий', value: summary?.sla_open_total || 0, sub: 'открытых', color: '#e04848' },
    { title: 'Маршрутов', value: summary?.routes_total || 0, sub: 'активных', color: '#0e9f6e' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        {metricCards.map(item => (
          <Card key={item.title} title={item.title} value={item.value} sub={item.sub} color={item.color} />
        ))}
      </div>

      {/* Live Bitrix */}
      <Section
        title="Live Bitrix"
        action={
          <Btn onClick={loadDetails} disabled={detailsLoading} tone="secondary">
            {detailsLoading ? 'Загружаем...' : 'Подгрузить live'}
          </Btn>
        }
      >
        <div style={{ fontSize: 13, color: 'var(--text2)' }}>
          Тяжёлые данные из Bitrix не блокируют открытие страницы. Жми кнопку когда нужно.
        </div>
        {detailsError ? <div style={{ marginTop: 10 }}><AlertBox tone="warning" message={detailsError} /></div> : null}
        {overview ? (
          <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
            <Card title="Непрочитанных чатов" value={liveSummary.unread_chats || 0} sub="live" color="#00a7dc" />
            <Card title="Открытых задач" value={liveSummary.open_tasks || 0} sub="live" color="#e08a00" />
            <Card title="SLA просрочек" value={liveSummary.sla_issues || 0} sub="live" color="#e04848" />
            <Card title="Соцсети в SLA" value={liveSummary.social_issues || 0} sub="live" color="#00a7dc" />
          </div>
        ) : null}
      </Section>

      {/* Control Center */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        <Section
          title="Пульт управления"
          action={
            <Btn onClick={runSla} disabled={slaRunning}>
              {slaRunning ? 'Проверяем...' : 'Проверить SLA'}
            </Btn>
          }
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <Btn onClick={() => runControl('full_reclassify')} disabled={Boolean(controlRunning)}>
              {controlRunning === 'full_reclassify' ? 'Выполняем...' : 'Разобрать очередь'}
            </Btn>
            <Btn onClick={() => runControl('return_red')} disabled={Boolean(controlRunning)}>
              {controlRunning === 'return_red' ? 'Выполняем...' : 'Вернуть в красную'}
            </Btn>
            <Btn onClick={() => runControl('rename_only')} disabled={Boolean(controlRunning)} tone="secondary">
              {controlRunning === 'rename_only' ? 'Выполняем...' : 'Переименовать + цена'}
            </Btn>
            <Btn onClick={() => runControl('spam_only')} disabled={Boolean(controlRunning)} tone="danger">
              {controlRunning === 'spam_only' ? 'Выполняем...' : 'Убрать спам'}
            </Btn>
          </div>
          {controlResult ? (
            <div style={{ marginTop: 12, background: 'var(--bg3)', borderRadius: 10, padding: '12px 14px', fontSize: 12 }}>
              {controlResult.error
                ? <span style={{ color: '#e04848' }}>{controlResult.error}</span>
                : <span>Режим: <b>{controlResult.mode}</b> · Проверено: <b>{controlResult.fetched_total || 0}</b> · Изменено: <b>{controlResult.updated_count || 0}</b> · Спам: <b>{controlResult.updated_preview?.filter(x => x.reason === 'spam').length || 0}</b></span>
              }
            </div>
          ) : null}
          {liveSla ? (
            <div style={{ marginTop: 10, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', fontSize: 12 }}>
              {liveSla.error
                ? <span style={{ color: '#e04848' }}>{liveSla.error}</span>
                : (
                  <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                    <div><span style={{ color: 'var(--text2)' }}>Проблем</span><br /><b>{liveSla.issues_total || 0}</b></div>
                    <div><span style={{ color: 'var(--text2)' }}>Филиалу</span><br /><b>{liveSla.branch_notifications_sent || 0}</b></div>
                    <div><span style={{ color: 'var(--text2)' }}>Рук-лю</span><br /><b>{liveSla.supervisor_notifications_sent || 0}</b></div>
                    <div><span style={{ color: 'var(--text2)' }}>Дайджестов</span><br /><b>{liveSla.digest_sent || 0}</b></div>
                  </div>
                )
              }
            </div>
          ) : null}
        </Section>

        {/* Leader tasks */}
        <Section title="Задачи руководителю">
          {leaderTasks.length === 0
            ? <div style={{ color: 'var(--text2)', fontSize: 13 }}>Открытых задач нет.</div>
            : leaderTasks.map(task => (
              <div key={task.id} style={{ background: 'var(--bg3)', border: `1px solid ${severityColor[task.severity] || 'var(--border)'}`, borderRadius: 10, padding: '12px 14px', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontWeight: 700, color: severityColor[task.severity] }}>{task.title}</span>
                  <Badge label={task.severity} color={severityColor[task.severity]} />
                </div>
                <div style={{ fontSize: 12, color: 'var(--text1)', marginTop: 6 }}>{task.description}</div>
              </div>
            ))
          }
        </Section>
      </div>

      {/* Masters */}
      <Section title="Точки" action={<Btn onClick={loadBootstrap} tone="ghost" size="sm">Обновить</Btn>}>
        {masters.length === 0
          ? <div style={{ color: 'var(--text2)', fontSize: 13 }}>Профили точек не заполнены.</div>
          : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
              {masters.map(branch => (
                <div key={branch.bitrix_user_id} style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)' }}>{branch.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text2)' }}>{branch.district || branch.city || '—'} · {branch.shift_status || 'available'}</div>
                    </div>
                    <div style={{ marginLeft: 'auto', fontSize: 22, fontWeight: 800, color: branch.score >= 80 ? '#0e9f6e' : branch.score >= 60 ? '#e08a00' : '#e04848' }}>
                      {branch.score}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {(branch.skills || []).map(skill => <Badge key={skill} label={skill} color="#00a7dc" />)}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 8, display: 'flex', gap: 12 }}>
                    <span>Активных: <b>{branch.active_leads}</b></span>
                    <span>Ответ: <b>{branch.first_response_avg_min} мин</b></span>
                  </div>
                </div>
              ))}
            </div>
          )
        }
      </Section>

      {/* Decisions */}
      <Section title="Последние решения">
        {decisions.length === 0
          ? <div style={{ color: 'var(--text2)', fontSize: 13 }}>Решений AI пока нет.</div>
          : decisions.map(item => (
            <div key={item.id} style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, color: 'var(--text)' }}>{item.customer_name || item.lead_external_id || '—'}</span>
                <Badge label={kindLabel[item.request_kind] || item.request_kind || 'general'} color="#00a7dc" />
                <Badge label={item.urgency || 'normal'} color={item.urgency === 'critical' ? '#e04848' : item.urgency === 'high' ? '#e08a00' : 'var(--text2)'} />
                <Badge label={item.completion_status || 'pending'} color={statusColor[item.completion_status] || 'var(--text2)'} />
                <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text2)' }}>{item.channel || '—'}</span>
              </div>
              {item.assigned_master_name ? (
                <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
                  → {item.assigned_master_name}
                </div>
              ) : null}
              <div style={{ fontSize: 12, color: 'var(--text1)', marginTop: 6 }}>{item.routing_reason || '—'}</div>
            </div>
          ))
        }
      </Section>
    </div>
  );
}

// ─── Tab: Симуляция ───────────────────────────────────────────────────────────

const INITIAL_SIM = {
  customer_name: '',
  phone: '',
  city: 'Санкт-Петербург',
  district: '',
  channel: 'manual_test',
  source: '',
  request_text: '',
  request_kind: '',
  priority_level: '',
  lead_external_id: '',
  deal_external_id: '',
};

function DecisionResult({ decision }) {
  if (!decision) return null;

  const isTarget = decision.is_target;
  const toneColor = decision.needs_human ? '#e08a00' : isTarget && decision.should_assign ? '#0e9f6e' : decision.risk_flags?.includes('spam') ? '#e04848' : 'var(--text2)';

  return (
    <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontWeight: 800, fontSize: 16, color: toneColor }}>
          {decision.should_assign ? '✅ Назначено' : decision.needs_human ? '🙋 Нужен человек' : decision.risk_flags?.includes('spam') ? '🚫 Спам' : '⚠️ Не квалифицирован'}
        </span>
        <Badge label={decision.request_kind || 'general'} color="#00a7dc" />
        <Badge label={decision.priority_level || 'normal'} color={decision.priority_level === 'critical' ? '#e04848' : '#e08a00'} />
        <Badge label={`зона: ${decision.routing_zone || '—'}`} color="#00a7dc" />
        {decision.expert_request ? <Badge label="Эксперт" color="#e08a00" /> : null}
      </div>

      <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: '12px 14px', fontSize: 13 }}>
        <div style={{ color: 'var(--text2)', marginBottom: 4 }}>Стадия до → после:</div>
        <div><b>{decision.stage_before}</b> → <b style={{ color: toneColor }}>{decision.stage_after}</b></div>
      </div>

      <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: '12px 14px', fontSize: 13 }}>
        <div style={{ color: 'var(--text2)', marginBottom: 4 }}>Причина решения:</div>
        <div>{decision.routing_reason}</div>
      </div>

      {decision.assigned_master ? (
        <div style={{ background: 'rgba(14,159,110,0.08)', border: '1px solid rgba(14,159,110,0.25)', borderRadius: 10, padding: '12px 14px', fontSize: 13 }}>
          <div style={{ color: '#0e9f6e', marginBottom: 4 }}>Назначен на точку:</div>
          <div style={{ fontWeight: 700, color: '#0e9f6e' }}>{decision.assigned_master.name}</div>
          <div style={{ color: 'var(--text2)', fontSize: 12, marginTop: 4 }}>
            {decision.assigned_master.district} · {(decision.assigned_master.skills || []).join(', ')}
          </div>
          <div style={{ color: 'var(--text2)', fontSize: 12, marginTop: 4 }}>Следующий шаг: <b>{decision.handoff_mode}</b></div>
        </div>
      ) : null}

      {decision.missing_fields?.length > 0 ? (
        <AlertBox tone="warning" message={`Не хватает полей: ${decision.missing_fields.join(', ')}`} />
      ) : null}

      {decision.risk_flags?.length > 0 ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {decision.risk_flags.map(f => <Badge key={f} label={f} color="#e04848" />)}
        </div>
      ) : null}

      {decision.scoreboard?.length > 0 ? (
        <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: '12px 14px' }}>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 8 }}>Scoreboard (топ {decision.scoreboard.length}):</div>
          {decision.scoreboard.map((item, i) => (
            <div key={item.bitrix_user_id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0',
              borderBottom: i < decision.scoreboard.length - 1 ? '1px solid var(--border)' : 'none',
            }}>
              <span style={{ fontSize: 11, color: 'var(--text2)', width: 16 }}>#{i + 1}</span>
              <span style={{ fontSize: 13, color: 'var(--text)', flex: 1 }}>{item.name}</span>
              <span style={{ fontSize: 12, color: 'var(--text2)' }}>{item.zone}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: item.score >= 80 ? '#0e9f6e' : '#e08a00' }}>{item.score}</span>
              <Badge label={item.is_open_now ? 'открыт' : 'закрыт'} color={item.is_open_now ? '#0e9f6e' : 'var(--text2)'} />
            </div>
          ))}
        </div>
      ) : null}

      {decision.conversation?.length > 0 ? (
        <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: '12px 14px' }}>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 8 }}>Сценарий разговора AI:</div>
          {decision.conversation.map((msg, i) => (
            <div key={i} style={{
              background: 'var(--bg2)',
              borderRadius: 8,
              padding: '8px 12px',
              marginBottom: 6,
              fontSize: 13,
              color: 'var(--text)',
              borderLeft: '3px solid #00a7dc',
            }}>
              {msg}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function TabSimulate() {
  const [form, setForm] = useState(INITIAL_SIM);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const set = (key) => (val) => setForm(prev => ({ ...prev, [key]: val }));

  const run = async () => {
    setRunning(true);
    setError('');
    setResult(null);
    try {
      const res = await api.post('/crm/bitrix-agent/simulate', form, { timeout: 20000 });
      setResult(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Ошибка симуляции.');
    } finally {
      setRunning(false);
    }
  };

  const presets = [
    { label: '🔑 Дубликат, север', values: { customer_name: 'Иван Петров', phone: '+79001234567', district: 'Выборгский', request_text: 'Хочу сделать дубликат ключа от квартиры', request_kind: 'duplicate' } },
    { label: '🚗 Автоключ, юг', values: { customer_name: 'Мария Сидорова', phone: '+79119876543', district: 'Московский', request_text: 'Нужен чип-ключ на Hyundai Santa Fe, оригинал потерян', request_kind: 'auto' } },
    { label: '🚨 Срочное вскрытие', values: { customer_name: 'Клиент', phone: '+79219999999', district: 'Центр', request_text: 'Заклинило замок, срочно не попасть домой прямо сейчас', request_kind: 'emergency', priority_level: 'critical' } },
    { label: '📦 Выезд на адрес', values: { customer_name: 'ООО Магазин', phone: '+78123333333', district: '', request_text: 'Нужно приехать в офис сделать ключи, нам неудобно ехать', request_kind: 'corporate' } },
    { label: '🚫 Спам', values: { customer_name: 'Аноним', phone: '', district: '', request_text: 'Предлагаю рекламу вашему бизнесу, вакансия менеджера', request_kind: '' } },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Section title="Симуляция решения агента">
        <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 14 }}>
          Запустить агента на тестовом лиде. Решение сохраняется в журнал (как обычная заявка). Используй пресеты или заполни форму вручную.
        </div>

        {/* Presets */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          {presets.map(p => (
            <Btn key={p.label} tone="ghost" size="sm" onClick={() => setForm(prev => ({ ...prev, ...p.values }))}>
              {p.label}
            </Btn>
          ))}
        </div>

        {/* Form */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Имя клиента">
            <Input value={form.customer_name} onChange={set('customer_name')} placeholder="Иван Петров" />
          </Field>
          <Field label="Телефон">
            <Input value={form.phone} onChange={set('phone')} placeholder="+79001234567" />
          </Field>
          <Field label="Город">
            <Input value={form.city} onChange={set('city')} placeholder="Санкт-Петербург" />
          </Field>
          <Field label="Район">
            <Input value={form.district} onChange={set('district')} placeholder="Выборгский, Московский..." />
          </Field>
          <Field label="Канал">
            <Select
              value={form.channel}
              onChange={set('channel')}
              options={[
                { value: 'manual_test', label: 'manual_test' },
                { value: 'openline', label: 'openline' },
                { value: 'phone', label: 'phone' },
                { value: 'whatsapp', label: 'whatsapp' },
                { value: 'telegram', label: 'telegram' },
              ]}
            />
          </Field>
          <Field label="Тип запроса (пусто = авто)">
            <Select
              value={form.request_kind}
              onChange={set('request_kind')}
              options={[
                { value: '', label: 'Авто-определение' },
                { value: 'duplicate', label: 'Дубликат ключа' },
                { value: 'auto', label: 'Автоключ / чип' },
                { value: 'emergency', label: 'Вскрытие' },
                { value: 'corporate', label: 'Корпоратив' },
                { value: 'general', label: 'Общий' },
              ]}
            />
          </Field>
          <Field label="Приоритет (пусто = авто)">
            <Select
              value={form.priority_level}
              onChange={set('priority_level')}
              options={[
                { value: '', label: 'Авто-определение' },
                { value: 'normal', label: 'Обычный' },
                { value: 'high', label: 'Высокий' },
                { value: 'critical', label: 'Критический' },
              ]}
            />
          </Field>
          <Field label="ID сделки в Bitrix (опц.)">
            <Input value={form.deal_external_id} onChange={set('deal_external_id')} placeholder="12345" />
          </Field>
        </div>

        <Field label="Текст запроса клиента">
          <Textarea
            value={form.request_text}
            onChange={set('request_text')}
            placeholder="Опишите запрос клиента своими словами..."
            rows={4}
          />
        </Field>

        <div style={{ marginTop: 16, display: 'flex', gap: 10, alignItems: 'center' }}>
          <Btn onClick={run} disabled={running}>
            {running ? '⏳ Агент думает...' : '▶ Запустить агента'}
          </Btn>
          <Btn tone="ghost" size="sm" onClick={() => { setForm(INITIAL_SIM); setResult(null); setError(''); }}>
            Сбросить
          </Btn>
        </div>

        {error ? <div style={{ marginTop: 12 }}><AlertBox tone="error" message={error} /></div> : null}
        <DecisionResult decision={result?.decision} />
      </Section>
    </div>
  );
}

// ─── Tab: Маршруты уведомлений ────────────────────────────────────────────────

const EMPTY_ROUTE = {
  bitrix_user_id: '',
  branch_name: '',
  branch_dialog_id: '',
  supervisor_name: '',
  supervisor_dialog_id: '',
  active: true,
  notes: '',
};

function TabRoutes() {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | route object
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveOk, setSaveOk] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/crm/bitrix-agent/notification-routes', { timeout: 10000 });
      setRoutes(res.data || []);
    } catch (err) {
      setError(err?.message || 'Ошибка загрузки.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setSaveError('');
    setSaveOk('');
    try {
      await api.post('/crm/bitrix-agent/notification-routes', editing, { timeout: 10000 });
      setSaveOk('Маршрут сохранён.');
      setEditing(null);
      await load();
    } catch (err) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Ошибка сохранения.');
    } finally {
      setSaving(false);
    }
  };

  const setField = (key) => (val) => setEditing(prev => ({ ...prev, [key]: val }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Section
        title="Маршруты уведомлений"
        action={
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn tone="ghost" size="sm" onClick={load} disabled={loading}>{loading ? 'Загрузка...' : 'Обновить'}</Btn>
            <Btn size="sm" onClick={() => { setEditing({ ...EMPTY_ROUTE }); setSaveError(''); setSaveOk(''); }}>+ Добавить</Btn>
          </div>
        }
      >
        <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 14 }}>
          Настройте, в какой чат Bitrix24 уходят уведомления о просроченных заявках для каждого филиала (ответственного).
          <br />
          <b>bitrix_user_id</b> — ID пользователя Битрикс24 (ответственный за лиды точки).
          <b> branch_dialog_id</b> — Dialog ID чата, куда писать уведомления (например, chatXXXXXX или userXX).
        </div>

        {error ? <AlertBox tone="error" message={error} /> : null}
        {saveOk ? <AlertBox tone="success" message={saveOk} /> : null}

        {/* Edit form */}
        {editing ? (
          <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 12, padding: 16, marginBottom: 16 }}>
            <div style={{ fontWeight: 700, marginBottom: 14, color: 'var(--text)' }}>
              {editing.bitrix_user_id ? `Редактировать: ${editing.branch_name || editing.bitrix_user_id}` : 'Новый маршрут'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Bitrix User ID *">
                <Input value={editing.bitrix_user_id} onChange={setField('bitrix_user_id')} placeholder="123" />
              </Field>
              <Field label="Название филиала">
                <Input value={editing.branch_name} onChange={setField('branch_name')} placeholder="02. Северный 7" />
              </Field>
              <Field label="Dialog ID чата филиала">
                <Input value={editing.branch_dialog_id} onChange={setField('branch_dialog_id')} placeholder="chat108138 или user123" />
              </Field>
              <Field label="Имя руководителя">
                <Input value={editing.supervisor_name} onChange={setField('supervisor_name')} placeholder="Иван Бобров" />
              </Field>
              <Field label="Dialog ID руководителя (эскалация)">
                <Input value={editing.supervisor_dialog_id} onChange={setField('supervisor_dialog_id')} placeholder="user456" />
              </Field>
              <Field label="Активен">
                <Select
                  value={editing.active ? 'true' : 'false'}
                  onChange={val => setField('active')(val === 'true')}
                  options={[{ value: 'true', label: 'Да' }, { value: 'false', label: 'Нет' }]}
                />
              </Field>
            </div>
            <Field label="Заметки">
              <Textarea value={editing.notes} onChange={setField('notes')} placeholder="Необязательный комментарий..." rows={2} />
            </Field>
            {saveError ? <div style={{ marginTop: 10 }}><AlertBox tone="error" message={saveError} /></div> : null}
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <Btn onClick={save} disabled={saving || !editing.bitrix_user_id}>
                {saving ? 'Сохраняем...' : 'Сохранить'}
              </Btn>
              <Btn tone="ghost" size="sm" onClick={() => { setEditing(null); setSaveError(''); }}>Отмена</Btn>
            </div>
          </div>
        ) : null}

        {/* Routes list */}
        {routes.length === 0 && !loading ? (
          <div style={{ color: 'var(--text2)', fontSize: 13 }}>Маршруты не настроены. Добавьте первый.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {routes.map(route => (
              <div key={route.id || route.bitrix_user_id} style={{
                background: 'var(--bg3)',
                border: `1px solid ${route.active ? 'var(--border)' : 'rgba(100,116,139,0.3)'}`,
                borderRadius: 10,
                padding: '12px 14px',
                opacity: route.active ? 1 : 0.6,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, color: 'var(--text)' }}>{route.branch_name || `User ${route.bitrix_user_id}`}</span>
                  <Badge label={route.active ? 'активен' : 'выключен'} color={route.active ? '#0e9f6e' : 'var(--text2)'} />
                  <Btn tone="ghost" size="sm" onClick={() => { setEditing({ ...route }); setSaveError(''); setSaveOk(''); }}>Изменить</Btn>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 6, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                  <span>Bitrix ID: <b>{route.bitrix_user_id}</b></span>
                  <span>Чат филиала: <b>{route.branch_dialog_id || '—'}</b></span>
                  <span>Руководитель: <b>{route.supervisor_dialog_id || '—'}</b></span>
                </div>
                {route.notes ? <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>{route.notes}</div> : null}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Help */}
      <Section title="Как найти Dialog ID в Bitrix24">
        <div style={{ fontSize: 13, color: 'var(--text1)', lineHeight: 1.7 }}>
          <ol style={{ margin: 0, paddingLeft: 20, color: 'var(--text1)' }}>
            <li>Откройте нужный чат в Bitrix24</li>
            <li>В URL будет что-то вроде: <code style={{ background: 'var(--bg3)', padding: '2px 6px', borderRadius: 4, color: '#00a7dc' }}>/im/view/chat/108138</code> — число после <code>/chat/</code> это ID</li>
            <li>Используйте формат: <b>chat108138</b></li>
            <li>Для личного сообщения пользователю: <b>user123</b> (где 123 — ID пользователя)</li>
          </ol>
          <div style={{ marginTop: 10, color: 'var(--text2)' }}>
            Можно также использовать REST API: <code style={{ color: '#00a7dc' }}>crm.chat.get</code> или посмотреть в <b>Пульт управления → Live Bitrix → Чаты</b>
          </div>
        </div>
      </Section>
    </div>
  );
}

// ─── Tab: Сессии ассистента ───────────────────────────────────────────────────

function TabSessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgsLoading, setMsgsLoading] = useState(false);
  const [approving, setApproving] = useState(null);
  const [approveNote, setApproveNote] = useState('');

  const loadSessions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/crm/bitrix-assistant/sessions?limit=50', { timeout: 10000 });
      setSessions(res.data || []);
    } catch (err) {
      setError(err?.message || 'Ошибка загрузки сессий.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const loadMessages = async (sessionId) => {
    setSelectedSession(sessionId);
    setMsgsLoading(true);
    try {
      const res = await api.get(`/crm/bitrix-assistant/messages?session_id=${sessionId}&limit=100`, { timeout: 10000 });
      setMessages(res.data || []);
    } catch {
      setMessages([]);
    } finally {
      setMsgsLoading(false);
    }
  };

  const approve = async (messageId) => {
    setApproving(messageId);
    try {
      await api.post(`/crm/bitrix-assistant/messages/${messageId}/approve`, { notes: approveNote }, { timeout: 15000 });
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, status: 'approved' } : m));
      setApproveNote('');
    } catch (err) {
      alert(err?.response?.data?.detail || err?.message || 'Ошибка аппрувинга.');
    } finally {
      setApproving(null);
    }
  };

  const modeColor = { auto_send: '#0e9f6e', draft_for_approval: '#e08a00', escalated: '#e04848' };
  const msgStatusColor = { approved: '#0e9f6e', pending: '#e08a00', sent: '#00a7dc', error: '#e04848' };

  return (
    <div style={{ display: 'flex', gap: 18 }}>
      {/* Sessions list */}
      <div style={{ width: 340, flexShrink: 0 }}>
        <Section
          title="Сессии ассистента"
          action={<Btn tone="ghost" size="sm" onClick={loadSessions} disabled={loading}>{loading ? '...' : 'Обновить'}</Btn>}
        >
          {error ? <AlertBox tone="error" message={error} /> : null}
          {sessions.length === 0 && !loading ? (
            <div style={{ color: 'var(--text2)', fontSize: 13 }}>
              Сессий пока нет. Они появятся когда агент начнёт обрабатывать сообщения из открытых линий Bitrix24.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 600, overflowY: 'auto' }}>
              {sessions.map(s => (
                <div
                  key={s.id}
                  onClick={() => loadMessages(s.id)}
                  style={{
                    background: selectedSession === s.id ? 'rgba(0,167,220,0.1)' : 'var(--bg3)',
                    border: `1px solid ${selectedSession === s.id ? '#00a7dc' : 'var(--border)'}`,
                    borderRadius: 10,
                    padding: '10px 12px',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)', flex: 1 }}>
                      {s.customer_name || s.dialog_id || `Сессия #${s.id}`}
                    </span>
                    <Badge label={s.mode || 'auto'} color={modeColor[s.mode] || 'var(--text2)'} />
                  </div>
                  {s.deal_external_id ? (
                    <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4 }}>Сделка #{s.deal_external_id}</div>
                  ) : null}
                  <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>
                    {s.channel || '—'} · {s.created_at ? new Date(s.created_at).toLocaleString('ru') : '—'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      {/* Messages */}
      <div style={{ flex: 1 }}>
        {!selectedSession ? (
          <div style={{ background: 'var(--bg2)', borderRadius: 14, border: '1px solid var(--border)', padding: 24, color: 'var(--text2)', fontSize: 13 }}>
            Выберите сессию слева, чтобы увидеть сообщения и управлять аппрувом черновиков.
          </div>
        ) : msgsLoading ? (
          <div style={{ background: 'var(--bg2)', borderRadius: 14, border: '1px solid var(--border)', padding: 24, color: 'var(--text2)' }}>
            Загружаем сообщения...
          </div>
        ) : (
          <Section title={`Сообщения сессии #${selectedSession}`}>
            {messages.length === 0 ? (
              <div style={{ color: 'var(--text2)', fontSize: 13 }}>Сообщений нет.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {messages.map(msg => (
                  <div key={msg.id} style={{ background: 'var(--bg3)', borderRadius: 10, padding: '12px 14px', border: `1px solid ${msg.status === 'pending' ? 'rgba(224,138,0,0.4)' : 'var(--border)'}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                      <Badge label={msg.role || 'unknown'} color={msg.role === 'assistant' ? '#00a7dc' : '#00a7dc'} />
                      <Badge label={msg.status || '—'} color={msgStatusColor[msg.status] || 'var(--text2)'} />
                      {msg.mode ? <Badge label={msg.mode} color={modeColor[msg.mode] || 'var(--text2)'} /> : null}
                      <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text2)' }}>
                        {msg.created_at ? new Date(msg.created_at).toLocaleString('ru') : ''}
                      </span>
                    </div>

                    <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                      {msg.content || msg.text || '—'}
                    </div>

                    {msg.ai_draft ? (
                      <div style={{ marginTop: 10, background: 'rgba(0,167,220,0.08)', border: '1px solid rgba(0,167,220,0.25)', borderRadius: 8, padding: '10px 12px' }}>
                        <div style={{ fontSize: 11, color: '#0089b6', marginBottom: 6 }}>Черновик AI:</div>
                        <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.6 }}>{msg.ai_draft}</div>
                      </div>
                    ) : null}

                    {msg.status === 'pending' && msg.role === 'assistant' ? (
                      <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Input
                          value={approveNote}
                          onChange={setApproveNote}
                          placeholder="Заметка к аппруву (опц.)"
                        />
                        <Btn onClick={() => approve(msg.id)} disabled={approving === msg.id}>
                          {approving === msg.id ? 'Отправляем...' : '✅ Одобрить и отправить'}
                        </Btn>
                      </div>
                    ) : null}

                    {msg.manager_summary ? (
                      <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text2)', borderTop: '1px solid var(--border)', paddingTop: 8, whiteSpace: 'pre-wrap' }}>
                        {msg.manager_summary}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </Section>
        )}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview', label: '📊 Обзор' },
  { id: 'simulate', label: '▶ Симуляция' },
  { id: 'routes', label: '🔔 Маршруты' },
  { id: 'sessions', label: '💬 Сессии' },
];

export default function BitrixAgent() {
  const [tab, setTab] = useState('overview');

  return (
    <div>
      <div className="kp-hero">
        <Plate>Битрикс24 · AI-агент</Plate>
        <h1 style={{ margin: 0 }}>Bitrix AI agent</h1>
        <p style={{ color: 'var(--text2)', margin: '8px 0 0', fontSize: 13 }}>
          Квалификация лидов · Маршрутизация по филиалам · SLA-мониторинг · Управление воронкой КПК—СПб
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 22, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              background: 'none',
              border: 'none',
              borderBottom: tab === t.id ? '2px solid #00a7dc' : '2px solid transparent',
              padding: '10px 18px',
              color: tab === t.id ? '#00a7dc' : 'var(--text2)',
              cursor: 'pointer',
              fontWeight: tab === t.id ? 700 : 400,
              fontSize: 14,
              transition: 'color 0.15s',
              marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'overview' && <TabOverview />}
      {tab === 'simulate' && <TabSimulate />}
      {tab === 'routes' && <TabRoutes />}
      {tab === 'sessions' && <TabSessions />}
    </div>
  );
}
